import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { usersApi, authApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Calendar,
  Wifi,
  LogOut,
  Map,
  History,
  List,
  Settings,
  Shield,
  ShoppingBag,
  Trash2,
  MapPin,
  KeyRound,
} from "lucide-react";
import { PasswordDialog } from "../components/PasswordDialog";

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function UsersPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    name: "",
    role: "vendedor",
  });
  const [creating, setCreating] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);

  const handlePasswordSubmit = ({ current, next }) =>
    passwordTarget.self
      ? authApi.changePassword({ current_password: current, new_password: next })
      : usersApi.resetPassword(passwordTarget.id, next);

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await usersApi.getAll();
        setUsers(response.data);
      } catch (error) {
        toast.error("Erro ao carregar usuários");
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    
    try {
      await usersApi.delete(deleteUserId);
      setUsers(users.filter(u => u.id !== deleteUserId));
      toast.success("Usuário excluído com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir usuário");
    } finally {
      setDeleteUserId(null);
      setDeleteUserName("");
    }
  };

  // Create user
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.name) {
      toast.error("Preencha todos os campos");
      return;
    }

    setCreating(true);
    try {
      const response = await authApi.register(newUser);
      setUsers([...users, response.data]);
      toast.success("Usuário criado com sucesso!");
      setShowAddModal(false);
      setNewUser({ username: "", password: "", name: "", role: "vendedor" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    } finally {
      setCreating(false);
    }
  };

  // Sidebar
  const Sidebar = () => (
    <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
            <Wifi className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
            <p className="text-xs text-slate-400">NOC Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? "bg-[#FF4500] text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="users-page">
      <Sidebar />

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Usuários</h2>
              <p className="text-slate-400">{users.length} usuários cadastrados</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPasswordTarget({ self: true })}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="change-my-password-button"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Alterar minha senha
              </Button>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
                data-testid="add-user-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Novo Usuário
              </Button>
            </div>
          </div>

          {/* Users Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Nome</TableHead>
                      <TableHead className="text-slate-400">Usuário</TableHead>
                      <TableHead className="text-slate-400">Perfil</TableHead>
                      <TableHead className="text-slate-400">Criado em</TableHead>
                      <TableHead className="text-slate-400 w-[120px]">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow
                        key={u.id}
                        className="border-slate-800 hover:bg-slate-800/50"
                        data-testid={`user-row-${u.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                u.role === "noc" ? "bg-purple-600" : "bg-blue-600"
                              }`}
                            >
                              {u.name?.charAt(0) || "U"}
                            </div>
                            <span className="text-white font-medium">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-slate-300">{u.username}</TableCell>
                        <TableCell>
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${
                              u.role === "noc"
                                ? "bg-purple-500/20 text-purple-400"
                                : "bg-blue-500/20 text-blue-400"
                            }`}
                          >
                            {u.role === "noc" ? (
                              <>
                                <Shield className="w-3 h-3" />
                                NOC
                              </>
                            ) : (
                              <>
                                <ShoppingBag className="w-3 h-3" />
                                Vendedor
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {new Date(u.created_at).toLocaleDateString("pt-BR")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.id !== user?.id && (
                            <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setPasswordTarget({ id: u.id, name: u.name })}
                              className="text-slate-400 hover:text-[#FF4500] hover:bg-[#FF4500]/10"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDeleteUserId(u.id);
                                setDeleteUserName(u.name);
                              }}
                              className="text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Usuário</DialogTitle>
            <DialogDescription className="text-slate-400">
              Crie uma nova conta de acesso ao sistema
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nome completo</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Nome do usuário"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Usuário (login)</Label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="usuario"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-user-username"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Senha</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Senha de acesso"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="new-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Perfil</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="vendedor" className="text-white">Vendedor</SelectItem>
                  <SelectItem value="noc" className="text-white">NOC (Admin)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creating}
              className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
              data-testid="confirm-create-user"
            >
              {creating ? "Criando..." : "Criar Usuário"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <PasswordDialog
        target={passwordTarget}
        onClose={() => setPasswordTarget(null)}
        onSubmit={handlePasswordSubmit}
      />
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tem certeza que deseja excluir o usuário <span className="font-bold text-white">{deleteUserName}</span>? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Excluir Usuário
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
